// react
import React, { useEffect, useState } from "react";
import { components } from "react-select";
import AsyncSelect from "react-select/async";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { client } from "../helpers/amplifyClient";
import { searchTokens } from "../helpers/api/searchTokens";
import {
  getCompanyById,
} from "./graphql/companySearch.queries";

// Custom Components for Select
const Control = ({ children, ...props }) => (
  <components.Control {...props}>
    <span className="mdi mdi-magnify search-icon"></span>
    {children}
  </components.Control>
);

const Option = (props) => (
  <components.Option {...props}>
    <div className="dropdown-item notify-item p-0">
      <i className="uil-suitcase font-16 me-1"></i>
      <span>{props.label}</span>
    </div>
  </components.Option>
);

const IndicatorsContainer = (props) => {
  const { selectProps } = props;
  const { showClear = true, inputValue, onClearSearch, hasSelection, isLoading } = selectProps || {};

  const canClear = showClear && (hasSelection || (inputValue && inputValue.length > 0));

  return (
    <components.IndicatorsContainer {...props}>
      {isLoading && (
        <span className="spinner-border spinner-border-sm text-muted me-1" role="status" />
      )}
      {canClear && (
        <div
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClearSearch && onClearSearch();
          }}
          style={{
            cursor: "pointer",
            padding: "0 8px",
            color: "#999",
            display: "flex",
            alignItems: "center",
          }}
          title="Clear"
        >
          <i className="mdi mdi-close" style={{ fontSize: "16px" }} />
        </div>
      )}
      {!canClear && !isLoading && <div style={{ backgroundColor: "#fff" }}></div>}
    </components.IndicatorsContainer>
  );
};

const MenuList = (props) => {
  const { t } = useTranslation(["components"]);
  return (
    <components.MenuList {...props}>
      <div className="dropdown-header noti-title">
        <h5 className="text-overflow mb-2 ms-2">
          {t("{{count}} result found", { count: props.children.length || 0, ns: "components" })}
        </h5>
      </div>
      {props.children}
    </components.MenuList>
  );
};

const NoOptionsMessage = (props) => {
  const { t } = useTranslation(["components"]);
  const { type } = props;
  return (
    <components.NoOptionsMessage {...props}>
      <span>{t("No results", { ns: "components" })}</span>
      {type === "supplier" && (
        <>
          <br />
          <a target="_blank" rel="noopener noreferrer" href="/supplier/create">
            {t("Create new Supplier", { ns: "components" })}
          </a>
        </>
      )}
    </components.NoOptionsMessage>
  );
};

/**
 * Universal Search Component
 *
 * Props:
 * - type: 'customer' | 'customerOrLead' | 'supplier' | 'company' | 'all'
 *   'customerOrLead' sucht Kunden und Leads gemeinsam und markiert Leads im Label.
 *   Für jeden Picker verwenden, in dem ein Lead ein legitimer Treffer ist —
 *   sonst legen Nutzer Dubletten an (siehe loadOptions).
 * - placeholder: string
 * - defaultValue: any
 * - onSelect: (value) => void
 * - allowLeads: boolean — Leads waehlbar machen. Nur fuer Picker, an denen ein Lead
 *   ein gueltiges Ziel ist (Aufgabe, Kontakt, Mail-Zuordnung). Bei Anfrage, Angebot
 *   und Artikel bleibt es beim Ausgrauen (A-V Punkt 28).
 * - isClearable: boolean
 * - showClear: boolean — X zum Leeren im Feld anzeigen. Nur setzen, wenn onSelect(null) verarbeitet wird.
 * - className: string
 * - label: string (for floating label)
 * - tNamespace: string | string[] (translation namespace)
 */

export const normalizeCompanyDefaultValue = (defaultValue) => {
  if (!defaultValue) return null;
  const label =
    defaultValue.label ||
    (defaultValue.number && defaultValue.name
      ? `${defaultValue.number} - ${defaultValue.name}`
      : defaultValue.name || "");
  const value = defaultValue.value || defaultValue.id || "";
  if (!label && !value) return null;
  return {
    label,
    value,
    data: defaultValue.data || defaultValue,
  };
};

const CompanySearch = ({
  type = "all",
  placeholder,
  defaultValue,
  onSelect,
  allowLeads = false,
  isClearable = true,
  showClear = true,
  className,
  label,
  tNamespace = ["components"],
}) => {
  const { t } = useTranslation(tNamespace);
  const [selectedOption, setSelectedOption] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const { user } = useSelector((state) => ({ user: state.Auth.user }));

  useEffect(() => {
    setSelectedOption(normalizeCompanyDefaultValue(defaultValue));
  }, [defaultValue]);

  const loadOptions = async (inputValue) => {
    if (!inputValue || inputValue.length < 2) return [];

    // Backend canRead handles role-based filtering:
    // countryUser → only own country, salesUser → only assigned
    //
    // "customerOrLead" sucht Kunden UND Leads. Ohne das entstehen Dubletten:
    // wer in einer Product Request seinen Kunden nicht findet, weil die Firma noch
    // als Lead erfasst ist, legt sie neu als Kunde an — und die Firma existiert
    // zweimal. searchTokens nimmt nur einen objectType, daher zwei Abfragen und
    // zusammenführen. ponytail: 2 Requests statt Schema- und Lambda-Änderung.
    const searchTypes =
      type === "customerOrLead" ? ["customer", "lead"] : [type];

    try {
      const results = await Promise.all(
        searchTypes.map((searchType) =>
          searchTokens({
            collectionName: "Companies",
            word: inputValue.toLowerCase(),
            ...(searchType !== "all" ? { objectType: searchType } : {}),
            limit: 25,
          }).then(({ items }) => items || []),
        ),
      );

      // Kunden zuerst: wenn dieselbe Firma beides ist, soll der Kunde oben stehen.
      return results.flat().map((item) => {
        const base = item.number
          ? `${item.number} - ${item.label}`
          : item.label;
        const isLead = item.objectType === "lead";
        return {
          // CRM A-V Punkt 28: Leads bleiben SICHTBAR, sind aber ausgegraut und nicht
          // wählbar — samt Hinweis, dass die Firma erst umzuwandeln ist.
          //
          // Sichtbar bleiben sie bewusst: wer den Lead nicht sieht, legt daneben
          // einen neuen Kunden an, und genau diese Dublette soll der Modus
          // customerOrLead verhindern. Ausgegraut, weil an einem Lead keine Anfrage
          // entsteht (Punkt 24 hat die stille Beförderung dafür entfernt).
          label: isLead
            ? allowLeads
              ? `${base} — ${t("Lead", { ns: "common" })}`
              : `${base} — ${t("Lead", { ns: "common" })} · ${t("Convert to customer first")}`
            : base,
          // react-select liest das über isOptionDisabled unten.
          isDisabled: isLead && !allowLeads,
          value: item.objectID,
          data: {
            id: item.objectID,
            number: item.number,
            name: item.label,
            type: item.objectType,
          },
        };
      });
    } catch (error) {
      console.error("Error fetching search results:", error);
      return [];
    }
  };

  const handleInputChange = (val, { action }) => {
    if (action === "input-change") {
      setInputValue(val);
    } else if (action === "set-value" || action === "menu-close") {
      setInputValue("");
    }
  };

  const handleClear = () => {
    setInputValue("");
    setSelectedOption(null);
    setIsFocused(false);
    onSelect && onSelect(null);
  };

  const handleChange = async (option) => {
    setInputValue("");
    if (!option) {
      setSelectedOption(null);
      onSelect && onSelect(null);
      return;
    }
    setSelectedOption(option);
    if (onSelect) {
      // salesUser fallback already includes full data; skip extra fetch
      if (option.data?.addresses) {
        onSelect(option);
        setIsFocused(false);
        return;
      }
      try {
        const res = await client.graphql({ query: getCompanyById, variables: { id: option.value } });
        const company = res.data.getCompanies;
        onSelect(company ? { ...option, data: company } : option);
      } catch {
        onSelect(option);
      }
      setIsFocused(false);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const onClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleClear();
  };

  return (
    <div className={classNames("app-search", className)}>
      {label && <label>{label}</label>}
      <AsyncSelect
        components={{
          Control,
          Option,
          MenuList,
          IndicatorsContainer,
          NoOptionsMessage: (props) => <NoOptionsMessage {...props} type={type} />,
          DropdownIndicator: null,
        }}
        showClear={showClear}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        hasSelection={!!selectedOption}
        onClearSearch={handleClear}
        loadingMessage={() => t("Loading...", { ns: "components" })}
        // CRM A-V Punkt 28: Leads sind sichtbar, aber nicht waehlbar. react-select
        // graut sie damit aus und laesst sie nicht anklicken.
        isOptionDisabled={(option) => option.isDisabled === true}
        options={selectedOption}
        placeholder={placeholder || t("Search...", { ns: "components" })}
        maxMenuHeight="350px"
        handleClick={onClick}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        value={isFocused ? null : selectedOption}
        isSearchable
        name="search-app"
        className="shipment-search dropdown react-select clear-separator"
        classNamePrefix="react-select"
        cacheOptions
        defaultOptions
        loadOptions={loadOptions}
        defaultValue={defaultValue ? defaultValue : null}
        isClearable={isClearable}
        closeMenuOnSelect
      />
    </div>
  );
};

export default CompanySearch;
